import React, { Fragment, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import {
  FaceMesh,
  FACEMESH_RIGHT_IRIS,
  FACEMESH_LEFT_IRIS,
} from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";
import { drawConnectors } from "@mediapipe/drawing_utils";

const WebcamImg = () => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [imgSrcs, setImgSrcs] = useState([]);
  const [PDValues, setPDValues] = useState([]);
  const [averageValue, setAverageValue] = useState(0);
  const [imageCount, setImageCount] = useState(0);
  const [showRetake, setShowRetake] = useState(false);

  const deviceWidth =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;

  let width = 640.0;
  let height = 480.0;

  if (deviceWidth < 670 && deviceWidth >= 510) {
    width = 480.0;
    height = 360.0;
  }
  if (deviceWidth < 510 && deviceWidth >= 390) {
    width = 360.0;
    height = 360.0;
  }
  if (deviceWidth < 390) {
    width = 320.0;
    height = 320.0;
  }

  const videoConstraints = {
    width: width,
    height: height,
    facingMode: "user",
  };

  const getDistance = (p1, p2) => {
    return Math.sqrt(
      Math.pow(p1.x - p2.x, 2) +
      Math.pow(p1.y - p2.y, 2) +
      Math.pow(p1.z - p2.z, 2)
    );
  };

  useEffect(() => {
    const onResults = (results) => {
      const canvasElement = canvasRef.current;
      canvasElement.width = width;
      canvasElement.height = height;
      const canvasCtx = canvasElement.getContext("2d");
      canvasCtx.save();
      canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
      canvasCtx.drawImage(results.image, 0, 0, canvasElement.width, canvasElement.height);

      if (results.multiFaceLandmarks && results.multiFaceLandmarks[0]) {
        let pupils = {
          left: {
            x: (results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[0][0]].x + results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[2][0]].x) / 2.0,
            y: (results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[0][0]].y + results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[2][0]].y) / 2.0,
            z: (results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[0][0]].z + results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[2][0]].z) / 2.0,
            width: getDistance(
              results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[0][0]],
              results.multiFaceLandmarks[0][FACEMESH_LEFT_IRIS[2][0]]
            ),
          },
          right: {
            x: (results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[0][0]].x + results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[2][0]].x) / 2.0,
            y: (results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[0][0]].y + results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[2][0]].y) / 2.0,
            z: (results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[0][0]].z + results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[2][0]].z) / 2.0,
            width: getDistance(
              results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[0][0]],
              results.multiFaceLandmarks[0][FACEMESH_RIGHT_IRIS[2][0]]
            ),
          },
        };

        let distance = getDistance(pupils.left, pupils.right);
        let irisWidthInMM = 12.0;
        let pupilWidth = Math.min(pupils.left.width, pupils.right.width);
        let pd = (irisWidthInMM / pupilWidth) * distance;

        setPDValues((prevPDValues) => [...prevPDValues, pd.toFixed(0)]);

        canvasCtx.fillStyle = "#4379b8";
        canvasCtx.fillRect(pupils.left.x * width - 2, pupils.left.y * height - 2, 4, 4);
        canvasCtx.fillRect(pupils.right.x * width - 2, pupils.right.y * height - 2, 4, 4);

        drawConnectors(canvasCtx, results.multiFaceLandmarks[0], FACEMESH_RIGHT_IRIS, { color: "#FF3030", lineWidth: 1 });
        drawConnectors(canvasCtx, results.multiFaceLandmarks[0], FACEMESH_LEFT_IRIS, { color: "#FF3030", lineWidth: 1 });
      }
      canvasCtx.restore();
    };

    const faceMesh = new FaceMesh({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });
    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    faceMesh.onResults(onResults);

    const videoElement = webcamRef.current;
    if (imgSrcs.length === 0 && typeof videoElement !== "undefined" && videoElement !== null) {
      const camera = new Camera(videoElement.video, {
        onFrame: async () => {
          await faceMesh.send({ image: videoElement.video });
        },
        width: width,
        height: height,
      });
      camera.start();
    }
  }, [imgSrcs, height, width, deviceWidth]);

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const data = canvas.toDataURL("image/png");
    setImgSrcs((prevImgSrcs) => [...prevImgSrcs, data]);

    setImageCount((prevCount) => prevCount + 1);

    if (imageCount + 1 === 3) {
      const average = PDValues.reduce((a, b) => parseFloat(a) + parseFloat(b), 0) / PDValues.length;
      setAverageValue(average.toFixed(0));
      setShowRetake(true);
    }
  };

  const resetPhoto = () => {
    setImgSrcs([]);
    setPDValues([]);
    setAverageValue(0);
    setImageCount(0);
    setShowRetake(false);
  };

  return (
    <Fragment>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 order-lg-last">
            <div className="container-display">
              <h5 className="text-center mb-2">Position head inside the oval on screen for accurate calculation.</h5>
              <p className="text-center mb-3">Take 3 Pictures and get accurate PD</p>
              <div className="container-video">
                <div className="oval"></div>
                <Webcam
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  width={width}
                  height={height}
                  audio={false}
                  imageSmoothing={true}
                  screenshotFormat="image/jpeg"
                  id="input-video"
                  className="result"
                />
                <canvas
                  ref={canvasRef}
                  id="output-canvas"
                  className="result"
                  style={{
                    width: width,
                    height: height,
                  }}
                ></canvas>
              </div>
            </div>

            {/* <div className="values text-center mb-3" style={{ display: showRetake ? 'block' : 'none' }}>
              <h3 className="h2 mb-4 pd_Count">{"Your PD is: " + averageValue + "mm"}</h3>
            </div> */}
            <div className="custom_row">
              <div className="container-img">
                {imgSrcs.map((src, index) => (
                  <div key={index} className="captured-img-wrap">
                    <img src={src} className="result" alt={`screenshot ${index + 1}`} />
                    <p>{`PD Value: ${PDValues[index]}mm`}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="values text-center mt-3" style={{ display: showRetake ? 'none' : 'block' }}>
              <button onClick={capturePhoto}>Take picture</button>
            </div>

            <div className="values text-center mt-3" style={{ display: showRetake ? 'block' : 'none' }}>
              <button onClick={resetPhoto}>Retake Pictures</button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default WebcamImg;
